/* eslint-disable no-shadow */
/* eslint-disable no-extra-boolean-cast */
/* eslint-disable no-plusplus */

import React, { createContext, useEffect, useMemo, useState } from 'react';
import { Route } from 'models/repossession/interfaces/route.interface';
import { Recovery } from 'models/repossession/interfaces/recovery.interface';
import { getUser } from 'localStorage/authStorage';
import {
	FetchRecoveries,
	FetchRoutes,
} from 'models/repossession/controllers/repossession.controller';
import { RouteStatus } from 'models/repossession/enums/route.enums';
import { PaginatedDocuments } from 'models/shared/types/pagination';
import { commonErrorHandler } from 'models/shared/helpers/errors';
import { RecoveryStatus } from 'models/repossession/enums/recovery.enums';
import { ClientReadableStream } from 'grpc-web';
import { Coordinate } from 'models/shared/types/basic';
import { getLocationByInternalId } from 'models/geozon/controllers/gps.controller';
import { InterestPointType } from 'models/repossession/enums/repossession.enums';
import { PositionServiceClient } from 'proto/service_grpc_web_pb';
import protoDefinition from 'proto/service_pb';

interface RepossessionMobileContextModel {
	loading: boolean;
	setLoading: React.Dispatch<React.SetStateAction<boolean>>;
	route?: Route;
	setRoute: React.Dispatch<React.SetStateAction<Route | undefined>>;
	recoveries: Recovery[];
	completedRecoveries: number;
	gpsLocations: { [key: string]: Coordinate };
	updateRecoveries: (recovery: Recovery) => void;
	clearContext: () => void;
	initializeContext: () => void;
}

export const repossessionMobileContext =
	createContext<RepossessionMobileContextModel>({
		loading: false,
	} as any);

export const RepossessionMobileContextProvider = ({
	children,
}: {
	children: any;
}) => {
	const [route, setRoute] = useState<Route>();
	const [recoveries, setRecoveries] = useState<Recovery[]>([]);
	const [loading, setLoading] = useState(false);

	const [completedRecoveries, setCompletedRecoveries] = useState(0);

	const [gpsStream, setGpsStream] =
		useState<ClientReadableStream<proto.position.PositionResponse>>();
	const [imeiMap, setImeiMap] = useState<{
		[key: string]: string;
	}>({});
	const [gpsLocations, setGpsLocations] = useState<{
		[key: string]: Coordinate;
	}>({});

	const clearContext = () => {
		setRoute(undefined);
		setRecoveries([]);
		setLoading(false);
		setCompletedRecoveries(0);
		initializeContext();
		setGpsLocations({});
		setImeiMap({});
	};

	const initializeContext = () => {
		const userId = getUser()?.userId;
		if (!userId) return;

		const locations: typeof gpsLocations = {};
		const imeis: typeof imeiMap = {};
		setLoading(true);
		FetchRoutes(1, [RouteStatus.pending, RouteStatus.inProgress], undefined, [
			userId,
		])
			.then(
				(
					resp: PaginatedDocuments<Route>,
				): Promise<PaginatedDocuments<Recovery> | undefined> => {
					const currentRoute = resp.data[0];
					if (currentRoute) {
						setRoute(currentRoute);
						return FetchRecoveries(currentRoute._id);
					}
					setRoute(undefined);
					return Promise.resolve(undefined);
				},
			)
			.then((currentRecoveries) => {
				const p: Promise<any>[] = [];
				if (currentRecoveries) {
					setRecoveries(
						currentRecoveries.data.sort((a, b) => a.index - b.index),
					);
					currentRecoveries.data.forEach((recovery) => {
						if (
							[
								InterestPointType.signalVehicle,
								InterestPointType.noSignalVehicle,
							].includes(recovery.interestPoint.type)
						) {
							p.push(
								getLocationByInternalId(
									recovery.repossession.loan.vehicle.internalId,
								).then((location) => {
									locations[recovery._id] = {
										long: location.coordinates[0],
										lat: location.coordinates[1],
									};
									imeis[location.imei] = recovery._id;
								}),
							);
						}
					});
				} else {
					setRecoveries([]);
				}
				return Promise.all(p);
			})
			.then(() => {
				setLoading(false);
				setGpsLocations(locations);
				setImeiMap(imeis);
			})
			.catch((e) => {
				setRoute(undefined);
				setRecoveries([]);
				commonErrorHandler(e);
				setLoading(false);
			});
	};

	useEffect(() => {
		if (gpsStream) {
			gpsStream.cancel();
			setGpsStream(undefined);
		}
		if (!Object.keys(imeiMap)?.length) return;

		const client = new PositionServiceClient(
			process.env.REACT_APP_GRPC_PROXY_URL ?? 'http://localhost:8080',
			null,
			null,
		);

		const request = new (protoDefinition as any).PositionRequest();
		Object.keys(imeiMap).forEach((imei) => {
			request.addImeis(imei, 0);
		});
		const locationsStream = client.streamPosition(request, null);
		locationsStream.on('data', (response: proto.position.PositionResponse) => {
			const imei = response.getImei();
			const coordinates = {
				lat: response.getLatitude(),
				long: response.getLongitude(),
			};
			const recoveryId = imeiMap[imei];
			setGpsLocations((prev) => {
				const locations = { ...prev };
				locations[recoveryId] = coordinates;
				return locations;
			});
		});
		setGpsStream(locationsStream);
	}, [imeiMap]);

	useEffect(() => {
		initializeContext();
	}, []);

	useEffect(() => {
		const notFinished = recoveries.filter((recovery) =>
			[
				RecoveryStatus.pending,
				RecoveryStatus.inProgress,
				RecoveryStatus.inSite,
			].includes(recovery.status),
		).length;
		setCompletedRecoveries(recoveries.length - notFinished);
	}, [recoveries]);

	const updateRecoveries = (recovery: Recovery) => {
		const newRecoveries = [...recoveries];
		const i = newRecoveries.findIndex((r) => r._id === recovery._id);
		if (i === -1) return;
		newRecoveries[i] = recovery;
		setRecoveries(newRecoveries);
	};

	const contextValue: RepossessionMobileContextModel = useMemo(
		() => ({
			loading,
			setLoading,
			route,
			setRoute,
			recoveries,
			completedRecoveries,
			gpsLocations,
			clearContext,
			updateRecoveries,
			initializeContext,
		}),
		[loading, route, recoveries, completedRecoveries, gpsLocations],
	);

	return (
		<repossessionMobileContext.Provider value={contextValue}>
			{children}
		</repossessionMobileContext.Provider>
	);
};
